/**
 * Unified Access Control Service
 *
 * Single source of truth for all access control checks in the application.
 * Handles module and permission-based access control with automatic normalization
 * of backend keys to frontend keys.
 *
 * @module accessControl
 */

import { authStorage } from './authStorage.service';
import {
  MODULE_KEY_MAPPING,
  PERMISSION_TO_MODULE,
  CORE_MODULES,
} from '../constants/moduleMapping.constants';

// ─────────────────────────────────────────────────────────────────────────────
// MODULE MAPPINGS
//
// MODULE_KEY_MAPPING, PERMISSION_TO_MODULE and CORE_MODULES are defined in
// src/constants/moduleMapping.constants.ts — edit ONLY that file to add or
// update backend ↔ frontend key mappings.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// ACCESS CONTROL SERVICE CLASS
// ─────────────────────────────────────────────────────────────────────────────

class AccessControlService {

  /**
   * Normalize a backend module key to frontend module key
   */
  normalizeModuleKey(backendKey: string): string {
    const normalized = MODULE_KEY_MAPPING[backendKey];
    return normalized || backendKey;
  }

  /**
   * Get user's modules from storage (normalized to frontend keys)
   */
  getUserModules(): string[] {
    const rawModules = authStorage.getModules();
    const normalizedModules = rawModules.map(m => this.normalizeModuleKey(m));

    // Add core modules that are always accessible
    const allModules = new Set([...normalizedModules, ...CORE_MODULES]);
    return Array.from(allModules);
  }

  /**
   * Get user's permissions from storage
   */
  getUserPermissions(): string[] {
    return authStorage.getPermissions();
  }

  /**
   * Derive modules from user's permissions
   * Used when backend sends permissions but not explicit modules
   */
  getModulesFromPermissions(): string[] {
    const permissions = this.getUserPermissions();
    const derivedModules = new Set<string>();

    permissions.forEach(permission => {
      const module = PERMISSION_TO_MODULE[permission];
      if (module) {
        derivedModules.add(module);
      }
    });

    return Array.from(derivedModules);
  }

  /**
   * Get all available modules (explicit + derived from permissions)
   */
  getAllAvailableModules(): string[] {
    const explicitModules = this.getUserModules();
    const derivedModules = this.getModulesFromPermissions();

    const allModules = new Set([...explicitModules, ...derivedModules]);
    return Array.from(allModules);
  }

  /**
   * Check if user has access to a specific module
   */
  hasModule(moduleKey: string): boolean {
    if (CORE_MODULES.includes(moduleKey)) {
      return true;
    }
    const availableModules = this.getAllAvailableModules();
    return availableModules.includes(moduleKey);
  }

  /**
   * Check if user has any of the specified modules
   */
  hasAnyModule(moduleKeys: string[]): boolean {
    return moduleKeys.some(key => this.hasModule(key));
  }

  /**
   * Check if user has all of the specified modules
   */
  hasAllModules(moduleKeys: string[]): boolean {
    return moduleKeys.every(key => this.hasModule(key));
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const userPermissions = this.getUserPermissions();
    return userPermissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    return permissions.some(p => userPermissions.includes(p));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    return permissions.every(p => userPermissions.includes(p));
  }

  /**
   * Combined access check: Module AND/OR Permission
   */
  hasAccess(moduleKey?: string, permission?: string): boolean {
    if (moduleKey && permission) {
      return this.hasModule(moduleKey) && this.hasPermission(permission);
    }
    if (moduleKey) {
      return this.hasModule(moduleKey);
    }
    if (permission) {
      return this.hasPermission(permission);
    }
    return true;
  }

  /**
   * Check if user is SuperAdmin or SystemAdmin (bypasses all checks)
   */
  isSuperAdmin(): boolean {
    const role = localStorage.getItem('role')?.toLowerCase();
    const roles = authStorage.getUserRoles()?.map(r => r.toLowerCase()) || [];

    return (
      role === 'superadmin' ||
      role === 'systemadmin' ||
      roles.includes('superadmin') ||
      roles.includes('systemadmin')
    );
  }

  /**
   * Debug: Log all access information
   */
  debugAccessInfo(): void {
    console.group('🔐 Access Control Debug Info');
    console.groupEnd();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT SINGLETON INSTANCE
// ─────────────────────────────────────────────────────────────────────────────

export const accessControl = new AccessControlService();

// Re-export mappings for any direct imports elsewhere in the codebase
export { MODULE_KEY_MAPPING, PERMISSION_TO_MODULE, CORE_MODULES };
